"use client";

import {
	Col as DSCol,
	Grid,
	Text,
	type ColProps
} from "@pamperedchef/design-system";
import { Children, type FC, type ReactNode } from "react";

import * as styles from "./ProductGrid.module.scss";

// `ColProps` picks up `React.HTMLAttributes` from a `framer-motion` import in
// the DS types, so `className` is missing from it — add it back on the cast.
const Col = DSCol as FC<
	ColProps & { children?: ReactNode; className?: string }
>;

const CARD_SPAN = { size: 6, sm: 6, md: 4 } as const;
const GUTTER = { xs: "md", md: "lg", xl: "xl" } as const;
const DEFAULT_EMPTY_MESSAGE = "No products match this category yet.";

export interface ProductGridProps {
	/** One card per child, already ordered by the caller. */
	children?: ReactNode;
	emptyMessage?: string;
	/** Below lg, cards after this many stay hidden. */
	shownBelowLg?: number;
}

/**
 * Layout for a row of product cards. Takes finished cards rather than product
 * data so a caller can supply one wired to app state, which a composite may
 * not reach.
 */
export function ProductGrid({
	children,
	emptyMessage = DEFAULT_EMPTY_MESSAGE,
	shownBelowLg = Number.POSITIVE_INFINITY
}: ProductGridProps): JSX.Element {
	if (Children.count(children) === 0) {
		return (
			<Text tag="p" className={styles["grid__empty"]}>
				{emptyMessage}
			</Text>
		);
	}

	return (
		<Grid
			columns={12}
			gap={GUTTER}
			className={styles["grid"]}
			data-component="ProductGrid"
		>
			{Children.map(children, (card, index) => (
				<Col
					{...CARD_SPAN}
					className={styles["grid__cell"]}
					data-beyond-below-lg={index >= shownBelowLg || undefined}
				>
					{card}
				</Col>
			))}
		</Grid>
	);
}
